import React, { useEffect, useLayoutEffect, useState } from 'react';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
//eslint-disable-next-line
import _ from 'lodash';
import { FaCheck } from 'react-icons/fa';
import ApplicationLayout from '../../components/layouts/ApplicationLayout/ApplicationLayout';
import Button from '../../components/buttons/Button';
import { isCommunity, isMoments } from '../../../constants';
import { BusinessType, CommunityType, MomentType } from '../../../types/business';
import { SubscriptionType } from '../../../types/misc';
import useTranslation from '../../../hooks/useBusinessTranslation';
import { CreateBusinessForm, CreateUserForm } from '../../../types/forms';
import NameInput from '../../components/forms/profile/NameInput';
import TermsAndConditions from '../../components/misc/TermsAndConditions';
import authService from '../../../services/authService';
import useAuth from '../../../hooks/account/useAuth';
import { businessTypeIcons } from '../../../utils/iconUtils';
import IconBadge from '../../components/icons/IconBadge';
import businessService from '../../../services/businessService';
import { wipeSelectedBusiness } from '../../../redux/slices/applicationSlice';
import { store } from '../../../redux/store';
import StepFlow, { StepFlowStep } from '../../components/misc/StepFlow';
import FeaturesForm from '../../components/misc/FeaturesForm';
import AreYouSureModal from '../../components/modals/AreYouSureModal';
import { getAffiliateByCode } from '../../../services/affiliateService';
import { isSubscriptionGreaterThanOrEqual } from '../../../utils/subscriptionUtils';
import {
  featuresInSubscription,
  getMinimumSubscriptionForFeatures,
} from '../../../utils/FeatureUtils';
import { SubscriptionSelector } from '../../components/selection-lists/SubscriptionSelector';

export default function OnboardingPage(): JSX.Element {
  const [step, setStep] = useState<number>(0);
  const [business, setBusiness] = useState<CreateBusinessForm>({ businessDescription: '' });

  const auth = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  const type = params.get('type') as BusinessType;

  const handleStep = (diff: 1 | -1) => setStep(step + diff);

  useLayoutEffect((): void => {
    if (location.state) {
      const data: CreateBusinessForm = location.state;

      setBusiness({ ...data, businessDescription: data.businessDescription || '' });
      setStep(isCommunity ? 3 : 2);
    } else if (type) {
      setBusiness((prevData: any) => ({ ...prevData, businessType: type }));
      setStep(2);
    }
  }, []);

  useEffect(() => {
    const code = params.get('code');
    if (code) {
      setBusiness((prevData: any) => ({ ...prevData, affiliateCode: code }));
    }
  }, [params]);

  useEffect(() => {
    store.dispatch(wipeSelectedBusiness());
  }, []);

  const steps = [
    !auth && <IntroductionView setStep={handleStep} />,
    <BusinessSetupView business={business} setBusiness={setBusiness} setStep={handleStep} />,
    isCommunity && (
      <FeaturesView business={business} setBusiness={setBusiness} setStep={handleStep} />
    ),
    <SubscriptionView business={business} setBusiness={setBusiness} setStep={handleStep} />,
    !auth && <LoginRegisterView business={business} setStep={handleStep} />,
  ].filter(Boolean) as JSX.Element[];

  return (
    <ApplicationLayout collapsed>
      <StepFlow steps={steps} indexState={[step, setStep]} />
    </ApplicationLayout>
  );
}

interface ViewProps {
  setStep: (step: 1 | -1) => void;
}

function IntroductionView({ setStep }: ViewProps) {
  const { t } = useTranslation();
  return (
    <StepFlowStep setStep={setStep} showPrev={false} className="max-w-4xl">
      <div className="mx-auto my-auto flex h-full w-full flex-col items-center justify-center gap-2 md:flex-row">
        <img
          alt="Vera Phone"
          src={`${process.env.PUBLIC_URL}/assets/figures/phone_hi.svg`}
          className="w-[50%]"
        />
        <div className="flex w-full flex-col gap-1">
          <h1 className="text-3xl font-semibold">{t('page.onboarding.introduction.title')}</h1>
          <p> {t('page.onboarding.introduction.subtitle')}</p>
        </div>
      </div>
    </StepFlowStep>
  );
}

type BusinessSetupViewProps = {
  business: CreateBusinessForm;
  setBusiness: (business: CreateBusinessForm) => void;
} & ViewProps;

function BusinessSetupView({ business, setBusiness, setStep }: BusinessSetupViewProps) {
  const { t } = useTranslation();
  const auth = useAuth();

  const typeOptions = (
    isMoments ? Object.keys(MomentType) : Object.keys(CommunityType)
  ) as BusinessType[];

  const getDefaultFeatures = (businessType: BusinessType) => {
    switch (businessType) {
      case BusinessType.ROOMMATES:
        return {
          EASY_SHARING: true,
          EVENTS: true,
          PARTICIPATION_INSIGHTS: true,
          TASKS: true,
          STATS_AND_SCORES: true,
          CHATS: true,
          COST_SPLITTING: true,
        };
      case BusinessType.STUDENTGROUP:
        return {
          EASY_SHARING: true,
          MEMBER_ADMINISTRATION: true,
          EVENTS: true,
          PARTICIPATION_INSIGHTS: true,
          STATS_AND_SCORES: true,
          COMMUNICATION: true,
          CHATS: true,
          COST_SPLITTING: true,
          PHOTO_ALBUMS: true,
        };
      case BusinessType.SPORTTEAM:
        return {
          EASY_SHARING: true,
          MEMBER_ADMINISTRATION: true,
          EVENTS: true,
          PARTICIPATION_INSIGHTS: true,
          TASKS: true,
          STATS_AND_SCORES: true,
          COMMUNICATION: true,
          CHATS: true,
          COST_SPLITTING: true,
        };
      case BusinessType.ASSOCIATION:
        return {
          EASY_SHARING: true,
          MEMBER_ADMINISTRATION: true,
          EVENTS: true,
          PARTICIPATION_INSIGHTS: true,
          TASKS: true,
          STATS_AND_SCORES: true,
          COMMUNICATION: true,
          CHATS: true,
          COST_SPLITTING: true,
          FINANCIAL_ADMINISTRATION: true,
          PHOTO_ALBUMS: true,
        };
      case BusinessType.COLLEAGUES:
        return {
          EASY_SHARING: true,
          EVENTS: true,
          TASKS: true,
          CHATS: true,
          COST_SPLITTING: true,
        };
      case BusinessType.FAMILY:
        return {
          EASY_SHARING: true,
          EVENTS: true,
          CHATS: true,
          COST_SPLITTING: true,
          PHOTO_ALBUMS: true,
        };
      case BusinessType.FRIENDS:
        return {
          EASY_SHARING: true,
          EVENTS: true,
          CHATS: true,
          COST_SPLITTING: true,
          PHOTO_ALBUMS: true,
        };
      default:
        return {};
    }
  };

  const handleTypeSelection = (type: BusinessType) => {
    const isDeselecting = type === business.businessType;
    const newBusinessType = isDeselecting ? undefined : type;
    const newFeatures = newBusinessType ? getDefaultFeatures(newBusinessType) : undefined;
    const requiredSubscription = newFeatures
      ? getMinimumSubscriptionForFeatures(newFeatures)
      : SubscriptionType.FREE;

    setBusiness({
      ...business,
      businessType: newBusinessType,
      features: newFeatures,
      connectedMembers: newFeatures ? newFeatures.EASY_SHARING : false,
      subscriptionType: requiredSubscription,
    });
  };

  return (
    <StepFlowStep
      setStep={setStep}
      disabled={!business.businessType || !business.businessName}
      showPrev={!auth}
      title={t('page.onboarding.type.title')}
      subtitle={t(`page.onboarding.type.subtitle`)}
      className="max-w-4xl">
      <div className="flex flex-col gap-6 pr-2">
        <div className="relative flex w-1/2 flex-shrink-0 flex-col gap-1">
          <div className="hidden items-start justify-between gap-1 sm:flex">
            <p className="font-semibold">{t(`page.onboarding.info.name`)}</p>
          </div>
          <div className="relative w-full">
            <div className="h-10 sm:hidden"> </div>
            <input
              type="text"
              className="w-full"
              placeholder={`${t('general.name')}...`}
              value={business.businessName || ''}
              onChange={(e) => setBusiness({ ...business, businessName: e.target.value })}
            />
            <p className="absolute right-4 top-2 text-sm sm:hidden">{t('general.mandatory')}</p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <p className="font-semibold">{t('page.onboarding.info.type')}</p>
          <div
            className="grid gap-3 lg:gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
            {typeOptions.map((type) => (
              <div
                key={type}
                onClick={() => handleTypeSelection(type)}
                className={classNames(
                  'group relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md',
                  {
                    'border-primary-900 bg-opacity-10 shadow-sm': business.businessType === type,
                    'border-secondary-200 bg-secondary-200 bg-opacity-30 hover:border-primary-900 hover:bg-opacity-40':
                      business.businessType !== type,
                  },
                )}>
                <div
                  className={classNames(
                    'absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all duration-200',
                    {
                      'border-primary-900 bg-primary-900': business.businessType === type,
                      'border-secondary-200 group-hover:border-primary-900':
                        business.businessType !== type,
                    },
                  )}>
                  {business.businessType === type && (
                    <FaCheck className="h-2.5 w-2.5 text-secondary-50" />
                  )}
                </div>

                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg transition-all duration-200">
                  <IconBadge
                    className={classNames({
                      'bg-primary-900 text-secondary-200': business.businessType === type,
                      'bg-secondary-200 text-primary-900': business.businessType !== type,
                    })}
                    icon={businessTypeIcons[type]}
                  />
                </div>

                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-primary-900">
                    {t(`page.onboarding.type.${type.toLowerCase().split('_')[0]}.name`)}
                  </h3>
                  <p className="text-xs leading-relaxed text-primary-900 opacity-75">
                    {t(`page.onboarding.type.${type.toLowerCase().split('_')[0]}.description`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </StepFlowStep>
  );
}

type FeaturesViewProps = {
  business: CreateBusinessForm;
  setBusiness: (business: CreateBusinessForm) => void;
} & ViewProps;

function FeaturesView({ business, setBusiness, setStep }: FeaturesViewProps) {
  const { t } = useTranslation();
  const [warningOpen, setWarningOpen] = React.useState(false);
  const [deferredStepChange, setDeferredStepChange] = React.useState<null | 1 | -1>(null);

  const wrappedSetStep = (diff: 1 | -1) => {
    const features = business.features || {};
    const anySelected = Object.values(features).some(Boolean);

    if (!anySelected && diff === 1) {
      setDeferredStepChange(diff);
      setWarningOpen(true);
    } else {
      setStep(diff);
    }
  };

  return (
    <>
      <StepFlowStep
        disabled={business.features === undefined}
        setStep={wrappedSetStep}
        title={t('page.onboarding.businessFeaturesScreen.title')}
        subtitle={t('page.onboarding.businessFeaturesScreen.subtitle')}
        className="max-w-4xl">
        <FeaturesForm
          features={business.features || {}}
          onFeaturesChange={(features) => {
            const requiredSubscription = getMinimumSubscriptionForFeatures(features);
            setBusiness({
              ...business,
              connectedMembers: features.EASY_SHARING,
              features,
              subscriptionType: requiredSubscription,
            });
          }}
        />
      </StepFlowStep>

      <AreYouSureModal
        open={warningOpen}
        setOpen={setWarningOpen}
        title={t('page.onboarding.businessFeaturesScreen.modal.title')}
        description={t('page.onboarding.businessFeaturesScreen.modal.description')}
        confirmLabel={t('page.onboarding.businessFeaturesScreen.modal.confirm')}
        cancelLabel={t('page.onboarding.businessFeaturesScreen.modal.cancel')}
        onConfirm={() => {
          setWarningOpen(false);
          if (deferredStepChange) {
            setStep(deferredStepChange);
            setDeferredStepChange(null);
          }
        }}
        onCancel={() => {
          setWarningOpen(false);
          setDeferredStepChange(null);
        }}
      />
    </>
  );
}

type SubscriptionViewProps = {
  business: CreateBusinessForm;
  setBusiness: (business: CreateBusinessForm) => void;
} & ViewProps;

function SubscriptionView({ business, setBusiness, setStep }: SubscriptionViewProps) {
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [downgradeWarningOpen, setDowngradeWarningOpen] = useState(false);
  const [pendingSubscription, setPendingSubscription] = useState<SubscriptionType | null>(null);
  const [params] = useSearchParams();

  const disableIncompatibleFeatures = () => {
    const selectedFeatures = { ...business.features };
    const allowedFeatures = featuresInSubscription[pendingSubscription ?? SubscriptionType.FREE];
    Object.keys(selectedFeatures).forEach((key) => {
      if (!allowedFeatures[key as keyof typeof allowedFeatures]) {
        selectedFeatures[key as keyof typeof selectedFeatures] = false;
      }
    });
    return selectedFeatures;
  };

  const handleSubscriptionSelection = (subscriptionType: SubscriptionType) => {
    const requiredSubscription = getMinimumSubscriptionForFeatures(business.features || {});
    if (!isSubscriptionGreaterThanOrEqual(subscriptionType, requiredSubscription)) {
      setPendingSubscription(subscriptionType);
      setDowngradeWarningOpen(true);
    } else {
      setBusiness({ ...business, subscriptionType });
    }
  };

  const handleConfirmDowngrade = () => {
    if (pendingSubscription) {
      const updatedFeatures = disableIncompatibleFeatures();

      setBusiness({
        ...business,
        subscriptionType: pendingSubscription,
        features: updatedFeatures,
        connectedMembers: updatedFeatures.EASY_SHARING || false,
      });
    }

    setDowngradeWarningOpen(false);
    setPendingSubscription(null);
  };

  const handleCancelDowngrade = () => {
    setDowngradeWarningOpen(false);
    setPendingSubscription(null);
  };

  const handleSubmit = async () => {
    if (business.affiliateCode) {
      await getAffiliateByCode(business.affiliateCode)
        .then(async (affiliate) => {
          if (!affiliate) {
            setError(t('toast.error.invalidAffiliateCode'));
            return;
          }
          await businessService.createBusiness(business);
          navigate('/overview');
        })
        .catch(() => {
          setError(t('toast.error.invalidAffiliateCode'));
        });
    } else {
      await businessService.createBusiness(business);
      navigate('/overview');
    }
  };

  return (
    <>
      <StepFlowStep
        setStep={setStep}
        disabled={!business.subscriptionType}
        showNext={!auth}
        onSubmit={auth ? handleSubmit : undefined}
        submitText={auth ? t('page.onboarding.confirmation.submit') : undefined}
        title={t('page.onboarding.subscription.title')}
        subtitle={t('page.onboarding.subscription.subtitle')}
        className="max-w-6xl">
        <SubscriptionSelector
          selectedSubscription={business.subscriptionType}
          mode="selectable"
          onSubscriptionSelect={handleSubscriptionSelection}
          showTrialPeriod
          trialDays={business.affiliateCode ? 60 : 30}
        />

        <div className="mt-6">
          <div className="w-1/2">
            <p>{t(`general.affiliateCode`)}</p>
            <input
              type="text"
              value={business.affiliateCode ?? ''}
              placeholder={`${t(`general.affiliateCode`)}...`}
              onChange={(e) => setBusiness({ ...business, affiliateCode: e.target.value })}
              readOnly={params.get('code') !== null}
            />
          </div>
          {error && <p className="mt-1 text-danger">{error}</p>}
        </div>
      </StepFlowStep>

      <AreYouSureModal
        open={downgradeWarningOpen}
        setOpen={setDowngradeWarningOpen}
        title={t('page.onboarding.subscription.downgrade.title')}
        description={t('page.onboarding.subscription.downgrade.description')}
        confirmLabel={t('page.onboarding.subscription.downgrade.confirm')}
        cancelLabel={t('page.onboarding.subscription.downgrade.cancel')}
        onConfirm={handleConfirmDowngrade}
        onCancel={handleCancelDowngrade}
      />
    </>
  );
}

type LoginRegisterViewProps = {
  business: CreateBusinessForm;
} & ViewProps;

function LoginRegisterView({ business, setStep }: LoginRegisterViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [view, setView] = useState<'LOGIN' | 'REGISTER'>('REGISTER');
  const [formData, setFormData] = useState<CreateUserForm>({});
  const [terms, setTerms] = useState<boolean>(false);

  const handleRegister = async (): Promise<void> => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error(t('toast.error.allFields'));
      return;
    }
    if (!terms) {
      toast.error(t('toast.error.terms'));
      return;
    }
    await authService.instantRegister(formData, business);
    navigate('/overview');
  };

  const handleLogin = async (): Promise<void> => {
    if (!formData.email) {
      toast.error(t('toast.error.allFields'));
      return;
    }
    await authService.generateMagicLink(formData.email, { business });
    toast.success(t('toast.success.sentMagicLink'));
  };

  return (
    <StepFlowStep
      showNext={false}
      setStep={setStep}
      onSubmit={handleRegister}
      disabled={!formData.email}
      submitText={t('page.onboarding.confirmation.submit')}
      title={t(`page.onboarding.confirmation.${view.toLowerCase()}.title`)}
      subtitle={t(`page.onboarding.confirmation.${view.toLowerCase()}.subtitle`)}>
      <div className="flex w-full flex-col gap-4">
        {view === 'REGISTER' && (
          <NameInput
            alias={`${formData.firstName || ''}+${formData.lastName || ''}`}
            setAlias={(alias: string) => {
              const [firstName, lastName] = alias.split('+');
              setFormData({ ...formData, firstName, lastName });
            }}
          />
        )}
        <div className="w-full">
          <p className="mb-1 pr-2 font-medium">Email</p>
          <input
            type="text"
            placeholder="Email"
            className="w-full"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </div>
        <div className="flex gap-1 text-sm text-primary-900">
          {view === 'REGISTER' ? (
            <>
              {t('auth.alreadyHaveAccount')}
              <Button className="px-0 py-0 underline" onClick={(): void => setView('LOGIN')}>
                {t('auth.login')}
              </Button>
            </>
          ) : (
            <>
              {t('auth.noAccountYet')}
              <Button className="px-0 py-0 underline" onClick={(): void => setView('REGISTER')}>
                {t('auth.register')}
              </Button>
            </>
          )}
        </div>
        {view === 'REGISTER' && <TermsAndConditions state={terms} setState={setTerms} />}
        {view === 'LOGIN' && (
          <div className="flex justify-end">
            <Button variant="primary" onClick={handleLogin}>
              {t('auth.sendLink')}
            </Button>
          </div>
        )}
      </div>
    </StepFlowStep>
  );
}
